import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  loadAndOpenLandingCampaignWidgetWithCoupon,
  loadAndOpenLandingCampaignWidgetForRanking,
  loadLandingCampaignWidget,
  openLandingCampaignWidget,
  unloadLandingCampaignWidget,
  type LoadLandingCampaignWidgetOptions,
  type LandingPendingCouponPrize,
} from '@/lib/landingCampaignWidget'
import { campaignSalesClosedReason, isCampaignOpenForSales } from '@/lib/campaign-sales'
import type { LandingCampaign } from '@/types/landing'

function profileCheckoutOptions(
  donorId?: number
): LoadLandingCampaignWidgetOptions | undefined {
  return donorId && donorId > 0
    ? { profileCheckout: true, profileDonorId: donorId }
    : undefined
}

export function useLandingCampaignWidget(
  campaign: LandingCampaign | null,
  donorId?: number
) {
  const campaignId = campaign?.id
  const identity = useMemo(
    () => Symbol(`${campaignId}:${donorId}`),
    [campaignId, donorId]
  )
  const [openingState, setOpeningState] = useState({ identity, value: false })
  const [errorState, setErrorState] = useState<{
    identity: symbol
    value: string | null
  }>({ identity, value: null })
  const isOpening = openingState.identity === identity && openingState.value
  const widgetError = errorState.identity === identity ? errorState.value : null
  const setIsOpening = useCallback(
    (value: boolean) => setOpeningState({ identity, value }),
    [identity]
  )
  const setWidgetError = useCallback(
    (value: string | null) => setErrorState({ identity, value }),
    [identity]
  )
  const opening = useRef(false)
  const owner = useRef<symbol | null>(null)

  useEffect(() => {
    owner.current = Symbol('checkout-owner')
    opening.current = false
    return () => {
      owner.current = null
      opening.current = false
      if (campaignId != null) {
        unloadLandingCampaignWidget(campaignId)
      }
    }
  }, [campaignId, donorId])

  const openPurchaseWidget = useCallback(() => {
    if (!campaign?.id || opening.current) {
      return Promise.resolve(false)
    }

    if (!isCampaignOpenForSales(campaign)) {
      setWidgetError(campaignSalesClosedReason(campaign))
      return Promise.resolve(false)
    }

    setWidgetError(null)
    const operationOwner = owner.current
    const canOpen = () => operationOwner !== null && owner.current === operationOwner
    opening.current = true
    setIsOpening(true)
    return loadLandingCampaignWidget(campaign, profileCheckoutOptions(donorId))
      .then(async (instanceId) => {
        if (!canOpen()) return false
        const opened = await openLandingCampaignWidget(instanceId, true, canOpen)
        if (!opened) throw new Error('widget_checkout_unavailable')
        return true
      })
      .catch(() => {
        if (!canOpen()) return false
        unloadLandingCampaignWidget(campaign.id)
        setWidgetError(
          'Não foi possível abrir a compra agora. Tente novamente em instantes.'
        )
        return false
      })
      .finally(() => {
        if (!canOpen()) return
        opening.current = false
        setIsOpening(false)
      })
  }, [campaign, donorId, setIsOpening, setWidgetError])

  const openPurchaseWidgetWithCoupon = useCallback(
    (coupon: LandingPendingCouponPrize): Promise<void> => {
      if (!campaign?.id || opening.current) {
        return Promise.reject(new Error('widget_busy'))
      }

      if (!isCampaignOpenForSales(campaign)) {
        const reason = campaignSalesClosedReason(campaign)
        setWidgetError(reason)
        return Promise.reject(new Error(reason))
      }

      setWidgetError(null)
      const operationOwner = owner.current
      const canOpen = () => operationOwner !== null && owner.current === operationOwner
      opening.current = true
      setIsOpening(true)
      return loadAndOpenLandingCampaignWidgetWithCoupon(
        campaign,
        coupon,
        profileCheckoutOptions(donorId),
        canOpen
      )
        .then(() => undefined)
        .catch((error) => {
          if (!canOpen()) throw error
          unloadLandingCampaignWidget(campaign.id)
          setWidgetError(
            'Não foi possível abrir a compra com cupom. Tente novamente em instantes.'
          )
          throw error
        })
        .finally(() => {
          if (!canOpen()) return
          opening.current = false
          setIsOpening(false)
        })
    },
    [campaign, donorId, setIsOpening, setWidgetError]
  )

  const openPurchaseWidgetForRanking = useCallback(
    (quantity: number, expectedAmountCents: number): Promise<void> => {
      if (!campaign?.id || opening.current)
        return Promise.reject(new Error('widget_busy'))
      if (!isCampaignOpenForSales(campaign)) {
        const reason = campaignSalesClosedReason(campaign)
        setWidgetError(reason)
        return Promise.reject(new Error(reason))
      }
      setWidgetError(null)
      const operationOwner = owner.current
      const canOpen = () => operationOwner !== null && owner.current === operationOwner
      opening.current = true
      setIsOpening(true)
      return loadAndOpenLandingCampaignWidgetForRanking(
        campaign,
        quantity,
        expectedAmountCents,
        profileCheckoutOptions(donorId),
        canOpen
      )
        .catch((error) => {
          if (!canOpen()) throw error
          if (
            error instanceof Error &&
            error.message === 'widget_ranking_payment_pending'
          ) {
            setWidgetError(
              'Já existe um Pix em andamento. Conclua ou cancele essa compra antes de escolher outra posição.'
            )
          } else {
            unloadLandingCampaignWidget(campaign.id)
            setWidgetError(
              'Não foi possível abrir este Pix. Tente novamente em instantes.'
            )
          }
          throw error
        })
        .finally(() => {
          if (!canOpen()) return
          opening.current = false
          setIsOpening(false)
        })
    },
    [campaign, donorId, setIsOpening, setWidgetError]
  )

  return {
    openPurchaseWidget,
    openPurchaseWidgetWithCoupon,
    openPurchaseWidgetForRanking,
    isWidgetLoading: isOpening,
    widgetError,
  }
}
